import React from "react";
import { Link } from "react-router-dom";
import { LightningIcon, UsersThreeIcon } from "@phosphor-icons/react";

const TARGET = 100;

/**
 * Two-mode social-proof banner:
 *  - Below the goal: scarcity ("Be the first 100 verified buyers — early bird perks").
 *  - Above the goal: pure social proof ("Join 1,200+ verified buyers").
 */
export default function VerifiedBuyersBanner({ count = 0 }) {
  if (count == null) return null;
  const reached = count >= TARGET;
  const remaining = Math.max(0, TARGET - count);

  return (
    <div
      className="w-full mt-12 mb-8 rounded-2xl border border-[#1D78AB]/30 bg-gradient-to-br from-[#0e1a2a] via-[#11253a] to-[#1a3554] px-6 md:px-10 py-5 md:py-6 flex items-center gap-4 md:gap-6 flex-wrap justify-center"
      data-testid="verified-buyers-banner"
    >
      <div className="h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-[#1D78AB]/20 grid place-items-center shrink-0">
        {reached ? (
          <UsersThreeIcon size={28} weight="fill" className="text-[#4FC3F7]" />
        ) : (
          <LightningIcon size={28} weight="fill" className="text-amber-300" />
        )}
      </div>

      <div className="flex-1 min-w-[260px] text-center md:text-left">
        {reached ? (
          <>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#4FC3F7] font-bold">
              Trusted by collectors
            </div>
            <div className="text-xl md:text-2xl font-display font-extrabold text-white mt-1">
              Join <span className="text-[#4FC3F7]">{count.toLocaleString()}+</span>{" "}
              verified buyers on Bidorah
            </div>
            <p className="text-sm text-zinc-300 mt-1">
              Strict ID-verified marketplace — no shill bidders, no flipping fees, no surprises.
            </p>
          </>
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold">
              Early bird · {remaining} {remaining === 1 ? "seat" : "seats"} left
            </div>
            <div className="text-xl md:text-2xl font-display font-extrabold text-white mt-1">
              Be one of the <span className="text-amber-300">first 100 verified buyers</span>
            </div>
            <p className="text-sm text-zinc-300 mt-1">
              Lock in founder-only perks: lower buyer fees, priority access to live drops, and
              a permanent founder badge on your profile.
            </p>
            <div className="mt-3 max-w-md">
              {/* Goal progress bar */}
              <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-300 to-amber-500 transition-all duration-700"
                  style={{ width: `${Math.min(100, (count / TARGET) * 100)}%` }}
                  data-testid="verified-buyers-progress"
                />
              </div>
              <div className="mt-1 flex justify-between text-[10px] text-zinc-400 font-mono uppercase tracking-wider">
                <span>{count} verified</span>
                <span>{TARGET} goal</span>
              </div>
            </div>
          </>
        )}
      </div>

      <Link
        to="/register?account_type=retail_buyer"
        className={`inline-flex items-center justify-center px-5 md:px-6 py-3 rounded-full font-bold text-sm transition shadow ${
          reached
            ? "bg-[#1D78AB] hover:bg-[#165d85] text-white"
            : "bg-amber-300 hover:bg-amber-200 text-zinc-900"
        }`}
        data-testid="verified-buyers-cta"
      >
        {reached ? "Join now →" : "Claim your seat →"}
      </Link>
    </div>
  );
}
